// ─── SurfaceBlock.js ──────────────────────────────────────────────────────────
// Shared accordion surface block with optional wrap mode calculator pad.

import React, { useState, useRef, useEffect } from 'react';
import { getSurfaceConfig, getWrapConfig, METHOD_CONFIG, METHOD_OPTIONS, SURFACE_CONFIG, GIRTH_DISPLAY, CONDITION_LEVELS } from './areaConstants';
import { familyForSubstrate, defectsForFamily, generateConditionNote } from './conditionLibrary';
import { surfaceUnit, surfaceTotal, hasData, n, DEFAULT_GIRTHS } from './calcUtils';

const safe = (arr) => Array.isArray(arr) ? arr : [];

// ── Qty stepper ──────────────────────────────────────────────────────────────
function QtyStepper({ value, onChange }) {
  const qty = parseInt(value) || 1;
  return (
    <div className="qty-stepper">
      <label>Qty</label>
      <div className="qty-stepper-row">
        <button className="qty-btn" onClick={() => onChange(String(Math.max(1, qty-1)))}>−</button>
        <span className="qty-val">{qty}</span>
        <button className="qty-btn" onClick={() => onChange(String(qty+1))}>+</button>
      </div>
    </div>
  );
}

const HORIZONTAL_SURFACES = new Set(['Floors', 'Ceilings', 'Soffit', 'Eaves', 'Stairs', 'Roofs']);
const LINEAR_SURFACES = new Set(['Skirting', 'Gutter', 'Fascia', 'Downpipes', 'Slab Edge']);

// ── Collapsed summary with inline dimensions ──────────────────────────────
function formatCollapsedSummary(substrate, base, entries, total, unit, wrapOn) {
  if (entries.length === 0 || total <= 0) return 'measuring...';
  if (wrapOn) return `${entries.length} entr${entries.length === 1 ? 'y' : 'ies'} — ${total.toFixed(2)} ${unit}`;

  const e = entries[0]; // primary entry
  const c = getSurfaceConfig(base);
  const isHorizontal = HORIZONTAL_SURFACES.has(base);
  const isLinear = LINEAR_SURFACES.has(base);

  let dims = '';

  if (c.isDoors) {
    const w = n(e.doorWidth) || 0.9;
    const h = n(e.doorHeight) || 2.1;
    const qty = parseInt(e.quantity) || 1;
    dims = `${w} \u00d7 ${h}` + (qty > 1 ? ` \u00d7${qty}` : '');
  } else if (c.isWindows) {
    const w = n(e.frameWidth) || 0;
    const h = n(e.frameLength) || 0;
    const qty = parseInt(e.quantity) || 1;
    dims = w && h ? `${w} \u00d7 ${h}` + (qty > 1 ? ` \u00d7${qty}` : '') : '';
  } else if (isLinear) {
    const l = n(e.length) || 0;
    dims = l ? `${l} LM` : '';
  } else {
    // Standard surface: height × length (vertical) or length × width (horizontal)
    const l = n(e.length) || 0;
    const h = n(e.height) || n(e.width) || 0;
    const qty = parseInt(e.quantity) || 1;
    if (l && h) {
      dims = isHorizontal ? `${l} \u00d7 ${h}` : `${h} \u00d7 ${l}`;
      if (qty > 1) dims += ` \u00d7${qty}`;
    } else if (l) {
      dims = `${l}m`;
    }
  }

  const more = entries.length > 1 ? ` +${entries.length - 1}` : '';
  const totalStr = `${total.toFixed(total < 10 ? 2 : 1)} ${unit}`;

  if (dims) return `${dims}${more} \u2192 ${totalStr}`;
  return `${entries.length} entr${entries.length === 1 ? 'y' : 'ies'} \u2014 ${totalStr}`;
}

// ── Standard entry form (non-wrap surfaces) ─────────────────────────────────
function EntryForm({ substrate, baseSurface, entry, index, total, onUpdate, onRemove, data, onSettingChange }) {
  const base = baseSurface || substrate;
  const c = getSurfaceConfig(base);
  const upd = (f, v) => onUpdate(f, v);
  const fw = parseFloat(entry.frameWidth||0), fl = parseFloat(entry.frameLength||0), qty = parseFloat(entry.quantity||1);
  const isHorizontal = HORIZONTAL_SURFACES.has(base);
  const isLinear = LINEAR_SURFACES.has(base);
  const isKnown = HORIZONTAL_SURFACES.has(base) || LINEAR_SURFACES.has(base) || ['Walls', 'Doors', 'Windows', 'Balustrade / Handrails', 'Posts'].includes(base);
  const dim2Label = isHorizontal ? 'Width (m)' : isKnown ? 'Height (m)' : 'Height / Width (m)';
  const dim2Field = isHorizontal ? 'width' : 'height';

  return (
    <div className={`ef-block ${total>1?"ef-block-multi":""}`}>
      {total>1 && (
        <div className="ef-block-header">
          <input className="ef-label-input" placeholder={`e.g. ${substrate} ${index+1}`} value={entry.label||""} onChange={e=>upd("label",e.target.value)} />
          <button className="ef-remove-btn" onClick={onRemove}>Remove</button>
        </div>
      )}
      {!c.isDoors && !c.isWindows && (
        <div className="s2s-meas-grid">
          {/* Vertical surfaces (Walls etc.): Height first, then Length */}
          {!isLinear && !isHorizontal && c.fields.includes('height') && <div className="s2s-field"><label>{dim2Label}</label><input type="number" inputMode="decimal" placeholder="—" value={entry[dim2Field]||""} onChange={e=>upd(dim2Field,e.target.value)} /></div>}
          {c.fields.includes('length') && <div className="s2s-field"><label>Length (m)</label><input type="number" inputMode="decimal" placeholder="—" value={entry.length||""} onChange={e=>upd("length",e.target.value)} /></div>}
          {/* Horizontal surfaces (Floors, Ceilings): Length first, then Width */}
          {!isLinear && isHorizontal && c.fields.includes('height') && <div className="s2s-field"><label>{dim2Label}</label><input type="number" inputMode="decimal" placeholder="—" value={entry[dim2Field]||""} onChange={e=>upd(dim2Field,e.target.value)} /></div>}
          {!isLinear && c.fields.includes('width') && !c.fields.includes('height') && <div className="s2s-field"><label>Width (m)</label><input type="number" inputMode="decimal" placeholder="—" value={entry.width||""} onChange={e=>upd("width",e.target.value)} /></div>}
          <QtyStepper value={entry.quantity} onChange={v => upd("quantity", v)} />
        </div>
      )}
      {c.isDoors && (
        <>
          <div className="s2s-meas-grid">
            <QtyStepper value={entry.quantity} onChange={v => upd("quantity", v)} />
            <div className="s2s-field"><label>Door W (m)</label><input type="number" inputMode="decimal" placeholder="0.9" value={entry.doorWidth} onChange={e=>upd("doorWidth",e.target.value)} /></div>
            <div className="s2s-field"><label>Door H (m)</label><input type="number" inputMode="decimal" placeholder="2.1" value={entry.doorHeight} onChange={e=>upd("doorHeight",e.target.value)} /></div>
          </div>
          <div className="s2s-field" style={{marginTop:8}}>
            <label>Which sides?</label>
            <div className="s2s-toggle-row">
              <button className={`s2s-toggle-btn ${entry.sides!=="both"?"active":""}`} onClick={()=>upd("sides","one")}>One side</button>
              <button className={`s2s-toggle-btn ${entry.sides==="both"?"active":""}`} onClick={()=>upd("sides","both")}>Both sides</button>
            </div>
          </div>
          <div className="s2s-checkbox-row" onClick={()=>upd("includeFrame",!entry.includeFrame)}>
            <div className={`s2s-checkbox ${entry.includeFrame?"checked":""}`}>{entry.includeFrame?"✓":""}</div>
            <span>Include door frame</span>
          </div>
          {entry.includeFrame && (
            <div className="s2s-field" style={{marginTop:8}}>
              <label>Frame Width (m) <span className="s2s-optional">default 0.05</span></label>
              <input type="text" inputMode="decimal" placeholder="0.05" value={entry.frameWidth} onChange={e=>upd("frameWidth",e.target.value)} />
            </div>
          )}
          {(() => {
            const dw = n(entry.doorWidth, 0.9), dh = n(entry.doorHeight, 2.1), s = entry.sides==='both' ? 2 : 1;
            const face = dw * dh * qty * s;
            const fw = n(entry.frameWidth, 0.05);
            const frame = entry.includeFrame ? 2 * (dw + dh) * fw * qty * s : 0;
            return (
              <div className="s2s-calc-preview">
                ≈ {(face + frame).toFixed(2)} m²
                {s === 2 && ' (both sides)'}
                {entry.includeFrame && ` (incl. frame ${fw}m)`}
              </div>
            );
          })()}
        </>
      )}
      {c.isWindows && (() => {
        const isLM = entry.windowMeasureType==='frame';
        const settings = data.surfaceSettings?.[substrate] || {};
        const g = parseFloat(settings.girth || (isLM ? 0.2 : 1));
        const perim = 2*fw+2*fl;
        const handleModeChange = (mode) => {
          upd("windowMeasureType", mode);
          // Auto-set girth: 0.2 for LM (frame width), 1 for m²
          onSettingChange(substrate, 'girth', mode === 'frame' ? 0.2 : 1);
        };
        return (
          <>
            <div className="s2s-field" style={{marginBottom:8}}>
              <label>Measure type</label>
              <div className="s2s-toggle-row">
                <button className={`s2s-toggle-btn ${!isLM?"active":""}`} onClick={()=>handleModeChange("area")}>m² (area)</button>
                <button className={`s2s-toggle-btn ${isLM?"active":""}`} onClick={()=>handleModeChange("frame")}>LM (frame girth)</button>
              </div>
            </div>
            <div className="s2s-meas-grid">
              <QtyStepper value={entry.quantity} onChange={v => upd("quantity", v)} />
              <div className="s2s-field"><label>Width (m)</label><input type="number" inputMode="decimal" placeholder="0.0" value={entry.frameWidth} onChange={e=>upd("frameWidth",e.target.value)} /></div>
              <div className="s2s-field"><label>Height (m)</label><input type="number" inputMode="decimal" placeholder="0.0" value={entry.frameLength} onChange={e=>upd("frameLength",e.target.value)} /></div>
            </div>
            {!isLM && (
              <>
                <div className="s2s-checkbox-row" onClick={()=>upd("includeFrame",!entry.includeFrame)}>
                  <div className={`s2s-checkbox ${entry.includeFrame?"checked":""}`}>{entry.includeFrame?"✓":""}</div>
                  <span>Include window frame</span>
                </div>
                {entry.includeFrame && (
                  <div className="s2s-field" style={{marginTop:8}}>
                    <label>Frame Width (m) <span className="s2s-optional">default 0.05</span></label>
                    <input type="text" inputMode="decimal" placeholder="0.05" value={entry.windowFrameWidth||''} onChange={e=>upd("windowFrameWidth",e.target.value)} />
                  </div>
                )}
              </>
            )}
            {!isLM && fw && fl && (() => {
              let total = fw * fl * qty;
              if (entry.includeFrame) {
                const wfw = parseFloat(entry.windowFrameWidth) || 0.05;
                total += 2 * (fw + fl) * wfw * qty;
              }
              return <div className="s2s-calc-preview">≈ {total.toFixed(2)} m²{entry.includeFrame && ' (incl. frame)'}</div>;
            })()}
            {isLM && fw && fl && <div className="s2s-calc-preview">≈ {(perim*qty).toFixed(2)} LM × {g} girth = {(perim*qty*g).toFixed(2)} m²</div>}
          </>
        );
      })()}
      {c.hasLanding && (
        <>
          <div className="s2s-checkbox-row" onClick={()=>upd("includeLanding",!entry.includeLanding)}>
            <div className={`s2s-checkbox ${entry.includeLanding?"checked":""}`}>{entry.includeLanding?"✓":""}</div>
            <span>Include stair landing</span>
          </div>
          {entry.includeLanding && (
            <div className="s2s-meas-grid" style={{marginTop:8}}>
              <div className="s2s-field"><label>Landing L</label><input type="number" inputMode="decimal" placeholder="0.0" value={entry.landingLength} onChange={e=>upd("landingLength",e.target.value)} /></div>
              <div className="s2s-field"><label>Landing W</label><input type="number" inputMode="decimal" placeholder="0.0" value={entry.landingWidth} onChange={e=>upd("landingWidth",e.target.value)} /></div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Calculator Pad (wrap mode) ──────────────────────────────────────────────
function CalculatorPad({ substrate, entries, girth, sharedDimKey, sharedDimLabel, sharedDimValue, onSharedDimChange, onAddRun, onRemoveEntry }) {
  const [val, setVal] = useState('');
  const [label, setLabel] = useState('');
  const inputRef = useRef(null);
  const sharedRef = useRef(null);

  // Track current values in refs so cleanup can access them
  const valRef = useRef(val);
  const labelRef = useRef(label);
  const entriesRef = useRef(entries);
  const onAddRunRef = useRef(onAddRun);
  const substrateRef = useRef(substrate);
  valRef.current = val;
  labelRef.current = label;
  entriesRef.current = entries;
  onAddRunRef.current = onAddRun;
  substrateRef.current = substrate;

  useEffect(() => {
    if (sharedDimKey && !sharedDimValue) sharedRef.current?.focus();
    else inputRef.current?.focus();
    // Auto-commit pending value on unmount (surface change, back, save)
    return () => {
      const v = valRef.current?.trim();
      if (v && !isNaN(parseFloat(v))) {
        const autoLabel = `${substrateRef.current.split(/[\s\-\u2013]/)[0]} ${entriesRef.current.length + 1}`;
        onAddRunRef.current(v, labelRef.current?.trim() || autoLabel);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = () => {
    const v = val.trim();
    if (!v || isNaN(parseFloat(v))) return;
    const autoLabel = `${substrate.split(/[\s\-\u2013]/)[0]} ${entries.length + 1}`;
    onAddRun(v, label.trim() || autoLabel);
    setVal(''); setLabel('');
    inputRef.current?.focus();
  };

  const realEntries = entries.filter(e => n(e.length) > 0);
  const g = girth || 1;
  const rawTotal = realEntries.reduce((s, e) => {
    const len = n(e.length), shared = n(sharedDimValue);
    return s + (sharedDimKey ? len * shared : len);
  }, 0);
  const total = rawTotal * g;
  const unit = sharedDimKey ? 'm²' : 'LM';
  const sharedReady = !sharedDimKey || (sharedDimValue && !isNaN(parseFloat(sharedDimValue)));

  return (
    <div className="calc-pad">
      {sharedDimKey && (
        <div className="calc-shared">
          <label className="calc-shared-label">{sharedDimLabel}</label>
          <input ref={sharedRef} className={`calc-shared-input ${sharedReady?'ready':''}`} type="text" inputMode="decimal" placeholder="e.g. 2.7"
            value={sharedDimValue||''} onChange={e => onSharedDimChange(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') inputRef.current?.focus(); }} />
        </div>
      )}
      <div className="calc-total-bar">
        <span className="calc-total-label">Running total</span>
        <span className="calc-total-val">
          {sharedReady ? `${total.toFixed(2)} ${unit}` : `${realEntries.length} run${realEntries.length!==1?'s':''} — set ${sharedDimLabel?.toLowerCase()||'dimension'}`}
        </span>
      </div>
      {realEntries.length > 0 && (
        <div className="calc-pills">
          {realEntries.map((e, i) => {
            const len = n(e.length);
            const m2 = (sharedDimKey ? len * n(sharedDimValue) : len) * g;
            return (
              <div key={e.id} className="calc-pill">
                <span className="calc-pill-label">{e.label || `#${i+1}`}</span>
                <span className="calc-pill-val">{len}m{m2>0 && sharedDimKey ? ` = ${m2.toFixed(1)}${unit}` : ''}</span>
                <button className="calc-pill-remove" onClick={() => onRemoveEntry(e.id)} aria-label="Remove entry">✕</button>
              </div>
            );
          })}
        </div>
      )}
      <div className="calc-entry">
        <input className="calc-label-input" placeholder="Label (optional)" value={label} onChange={e => setLabel(e.target.value)}
          onKeyDown={e => e.key==='Enter' && inputRef.current?.focus()} />
        <div className="calc-input-row">
          <input ref={inputRef} className="calc-num-input" type="text" inputMode="decimal"
            placeholder="Length (m)" value={val} onChange={e => setVal(e.target.value)}
            onKeyDown={e => e.key==='Enter' && commit()} />
          <button className={`calc-add-btn ${val&&!isNaN(parseFloat(val))?'ready':''}`} onClick={commit}>＋</button>
        </div>
      </div>
    </div>
  );
}

// ── Girth input — stores as text, only validates on blur ────────────────────
function GirthInput({ value, onChange }) {
  const [text, setText] = useState(String(value ?? ''));
  useEffect(() => { setText(String(value ?? '')); }, [value]);
  return (
    <input type="text" inputMode="decimal" value={text}
      onChange={e => setText(e.target.value)}
      onBlur={() => {
        const v = parseFloat(text);
        onChange(!isNaN(v) && v > 0 ? v : (value ?? 1));
      }}
      style={{width:60}} />
  );
}

// ── Main SurfaceBlock ───────────────────────────────────────────────────────
export default function SurfaceBlock({ substrate, data, isExpanded, onToggleExpand, onUpdate, onAdd, onRemove, onRemoveSurface, onSettingChange, groupId, areaType, onAddIndependent, onRenameSurface, onConditionChange, onCaptureForSubstrate }) {
  const settings = data.surfaceSettings?.[substrate] || {};
  const condEntry = data.conditionBySubstrate?.[substrate] || {};
  const condKey = condEntry.condition || null;
  const condDefects = Array.isArray(condEntry.defects) ? condEntry.defects : [];
  const condCustom = Array.isArray(condEntry.customDefects) ? condEntry.customDefects : [];
  // Photos tagged to this surface — shown inline so evidence persists across visits.
  const surfacePhotos = (data.photos || []).filter(p => p.substrate === substrate);
  const photoException = condEntry.photoExceptionReason || null;
  const condFamily = familyForSubstrate(substrate, settings.baseSurface, areaType);
  const availableDefects = defectsForFamily(condFamily);
  const base = settings.baseSurface || substrate;
  // Renameable: independent blocks (baseSurface set) or custom surfaces (not in standard config)
  const isRenameable = !!settings.baseSurface || !SURFACE_CONFIG[substrate];
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState(substrate);
  const commitRename = () => {
    const trimmed = renameVal.trim();
    if (trimmed && trimmed !== substrate && onRenameSurface) onRenameSurface(substrate, trimmed);
    setRenaming(false);
  };
  const c = getSurfaceConfig(base);
  const wrapCfg = getWrapConfig(base);
  const canWrap = !!wrapCfg && !c.isDoors && !c.isWindows;
  const wrapOn = !!settings._wrapMode;
  const isExternal = areaType === 'external';

  const DEFAULT_SPEED = { 'Doors': 6, 'Door frames': 6, 'Balustrades/Handrails': 6, 'Balustrade / Handrails': 6, 'Stairs': 12 };
  const defaultSpeed = DEFAULT_SPEED[base] || 16;
  const defaultCoats = c.isDoors ? 3 : 2;

  const entries = safe(data.surfaceEntries).filter(e => e.substrate === substrate && (groupId ? e.repeatGroupId === groupId : !e.repeatGroupId));
  const realEntries = entries.filter(hasData);
  const girth = settings.girth ?? DEFAULT_GIRTHS[base] ?? 1;
  const total = surfaceTotal(substrate, realEntries, girth);
  const unit = surfaceUnit(substrate, base);

  // Collapsed summary
  if (!isExpanded) {
    const methodKey = settings.method || 'brush_roll';
    const methodCfg = METHOD_CONFIG[methodKey];

    // Calculate hours for collapsed summary
    const rate = settings.paintingSpeed ?? defaultSpeed;
    const coats = settings.coats ?? defaultCoats;
    const prepRate = settings.prepRate ?? 75;
    const methodPrep = settings.methodPrep ?? 0;
    let surfHours = 0;
    if (total > 0) {
      const paintHrs = total * coats / rate;
      const prepHrs = prepRate > 0 ? total / prepRate : 0;
      surfHours = paintHrs + prepHrs + (paintHrs + prepHrs) * methodPrep;
    }

    return (
      <div className="sf-block sf-collapsed" onClick={onToggleExpand}>
        <div className="sf-collapsed-row">
          <span className="sf-collapsed-name">
            {substrate}
            {methodKey !== 'brush_roll' && <span className="sf-method-badge">{methodCfg.icon}</span>}
            {isRenameable && (
              <button className="sf-rename-btn" title="Rename surface"
                onClick={e => { e.stopPropagation(); setRenaming(true); setRenameVal(substrate); onToggleExpand(); }}>
                ✎
              </button>
            )}
          </span>
          <span className="sf-collapsed-summary">
            {realEntries.length > 0
              ? `${formatCollapsedSummary(substrate, base, realEntries, total, unit, wrapOn)}${surfHours > 0 ? ` \u00b7 ${surfHours.toFixed(1)}h` : ''}`
              : '— no measurements'}
          </span>
          <span className="sf-expand-arrow">▸</span>
        </div>
      </div>
    );
  }

  return (
    <div className="sf-block sf-expanded">
      <div className="sf-block-header" onClick={onToggleExpand}>
        <div style={{display:'flex',alignItems:'center',gap:6,flex:1,minWidth:0}}>
          {isRenameable && renaming ? (
            <input
              className="sf-rename-input"
              value={renameVal}
              autoFocus
              onClick={e => e.stopPropagation()}
              onChange={e => setRenameVal(e.target.value)}
              onBlur={commitRename}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
                if (e.key === 'Escape') { setRenaming(false); setRenameVal(substrate); }
              }}
            />
          ) : (
            <>
              <span className="sf-name">{substrate}</span>
              {isRenameable && (
                <button className="sf-rename-btn" title="Rename surface"
                  onClick={e => { e.stopPropagation(); setRenaming(true); setRenameVal(substrate); }}>
                  ✎
                </button>
              )}
            </>
          )}
          {total > 0 && !renaming && <span className="sf-header-total">{total.toFixed(2)} {unit}</span>}
        </div>
        <div className="sf-header-btns" onClick={e => e.stopPropagation()}>
          {canWrap && (
            <button className={`sf-wrap-toggle ${wrapOn?'active':''}`}
              onClick={() => onSettingChange(substrate, '_wrapMode', !wrapOn)}
              title={wrapOn ? 'Switch to standard' : 'Switch to rapid entry'}>
              {wrapOn ? '⟳ Rapid' : '⟳'}
            </button>
          )}
          <span className="sf-expand-arrow" onClick={onToggleExpand} title="Minimise">▾</span>
        </div>
      </div>
      <div className="sf-settings-row">
        <label className="sf-setting"><span>Speed</span>
          <select value={settings.paintingSpeed??defaultSpeed} onChange={e=>onSettingChange(substrate,'paintingSpeed',Number(e.target.value))}>
            <option value={30}>Spray</option><option value={25}>Very Fast</option><option value={20}>Fast</option><option value={16}>Normal</option><option value={12}>Abseil</option><option value={10}>Slow</option><option value={6}>Very Slow</option>
          </select>
        </label>
        {/* Girth/Pitch — conditionally shown based on GIRTH_DISPLAY config */}
        {(() => {
          const gd = GIRTH_DISPLAY[base];
          // Pitch dropdown (Roofs)
          if (c.hasPitch) return (
            <label className="sf-setting"><span>Pitch</span>
              <select value={settings.girth ?? DEFAULT_GIRTHS[base] ?? 1.15} onChange={e => onSettingChange(substrate, 'girth', Number(e.target.value))}>
                <option value={1.35}>Very Steep</option>
                <option value={1.25}>Steep</option>
                <option value={1.15}>Std</option>
                <option value={1.05}>Shallow</option>
                <option value={1}>Flat</option>
              </select>
            </label>
          );
          // Hidden — no girth control shown at all
          if (!gd || gd.visible === false) return null;
          // Inline surfaces show their dimension field separately (below settings row)
          if (gd.inline) return null;
          // Settings row girth with custom label (e.g. "Access Factor" for Eaves)
          return (
            <label className="sf-setting"><span>{gd.settingsLabel || 'Girth'}</span>
              <GirthInput value={settings.girth ?? DEFAULT_GIRTHS[base] ?? 1} onChange={v => onSettingChange(substrate, 'girth', v)} />
            </label>
          );
        })()}
        <label className="sf-setting"><span>Prep</span>
          <select value={settings.prepRate||75} onChange={e=>onSettingChange(substrate,'prepRate',Number(e.target.value))}>
            <option value={25}>Very High</option><option value={40}>High</option><option value={75}>Std</option><option value={100}>Low</option><option value={200}>Very Low</option>
          </select>
        </label>
        <label className="sf-setting"><span>Coats</span>
          <select value={settings.coats ?? defaultCoats} onChange={e=>onSettingChange(substrate,'coats',Number(e.target.value))}>
            {isExternal && <option value={0}>Wash only</option>}<option value={1}>1 coat</option><option value={1.125}>1.125 coats</option><option value={1.5}>1.5 coats</option><option value={2}>2 coats</option><option value={3}>3 coats</option><option value={4}>4 coats</option>
          </select>
        </label>
      </div>
      {onConditionChange && (
        <div className="sf-condition-row">
          <span className="sf-condition-label">Condition</span>
          <div className="sf-condition-pills">
            {CONDITION_LEVELS.map(c => (
              <button
                key={c.key}
                type="button"
                className={`sf-cond-pill sf-cond-${c.key} ${condKey === c.key ? 'active' : ''}`}
                onClick={() => onConditionChange(substrate, 'condition', condKey === c.key ? null : c.key)}
              >
                {c.label}
              </button>
            ))}
          </div>
          {condKey && (
            <div className="sf-defects-block">
              <div className="sf-defects-label">Defects observed</div>
              <div className="sf-defect-chips">
                {availableDefects.map(d => {
                  const on = condDefects.includes(d.id);
                  return (
                    <button
                      key={d.id}
                      type="button"
                      className={`sf-defect-chip ${on ? 'active' : ''} ${d.makeGood ? 'mk' : ''}`}
                      onClick={() => {
                        const next = on
                          ? condDefects.filter(x => x !== d.id)
                          : [...condDefects, d.id];
                        onConditionChange(substrate, 'defects', next);
                      }}
                    >
                      {d.label}
                    </button>
                  );
                })}
                {condCustom.map((txt, i) => (
                  <span key={'cust'+i} className="sf-defect-chip active sf-defect-custom">
                    {txt}
                    <button type="button" className="sf-chip-x" aria-label="Remove custom defect"
                      onClick={() => onConditionChange(substrate, 'customDefects', condCustom.filter((_, j) => j !== i))}>×</button>
                  </span>
                ))}
                <button type="button" className="sf-defect-chip sf-defect-add"
                  onClick={() => {
                    const txt = (window.prompt('Describe the condition issue:') || '').trim();
                    if (txt) onConditionChange(substrate, 'customDefects', [...condCustom, txt]);
                  }}>+ Add your own</button>
              </div>
              {/* Condition photo evidence for this surface — shows existing tagged photos
                  and always lets the assessor add more (or log a reason if none). */}
              <div className={`sf-photo-evidence ${surfacePhotos.length ? '' : 'sf-photo-needed'}`}>
                {surfacePhotos.length > 0 && (
                  <div className="sf-cond-photo-strip">
                    {surfacePhotos.map(p => (
                      <div key={p.id} className={`sf-cond-photo-thumb ${p.pending ? 'pending' : ''}`}>
                        <img src={p.thumbnailUrl || p.url} alt="condition evidence" />
                        {p.pending && <span className="sf-cond-photo-spin"><span className="spinner spinner-sm spinner-white"></span></span>}
                      </div>
                    ))}
                  </div>
                )}
                <div className="sf-photo-nudge-actions">
                  {surfacePhotos.length === 0 && (
                    <span className="sf-photo-nudge-text">📷 Add a photo of this surface as condition evidence.</span>
                  )}
                  {typeof onCaptureForSubstrate === 'function' && (
                    <button type="button" className="sf-photo-nudge-btn"
                      onClick={() => onCaptureForSubstrate(substrate)}>
                      {surfacePhotos.length > 0 ? '+ Add another photo' : 'Add photo'}
                    </button>
                  )}
                  {surfacePhotos.length === 0 && !photoException && (
                    <select className="sf-photo-exception"
                      value=""
                      onChange={e => { if (e.target.value) onConditionChange(substrate, 'photoExceptionReason', e.target.value); }}>
                      <option value="">No photo — reason…</option>
                      <option value="no_access">No access</option>
                      <option value="not_reachable">Not reachable</option>
                      <option value="camera_unavailable">Camera unavailable</option>
                    </select>
                  )}
                </div>
                {photoException && surfacePhotos.length === 0 && (
                  <div className="sf-photo-exception-set">
                    Photo skipped: {photoException === 'no_access' ? 'No access' : photoException === 'not_reachable' ? 'Not reachable' : 'Camera unavailable'}
                    <button type="button" className="sf-chip-x" aria-label="Clear reason"
                      onClick={() => onConditionChange(substrate, 'photoExceptionReason', null)}>×</button>
                  </div>
                )}
              </div>
              <div className="sf-defects-actions">
                <button
                  type="button"
                  className="sf-gen-note-btn"
                  onClick={() => onConditionChange(
                    substrate, 'note',
                    generateConditionNote(substrate, { ...condEntry, condition: condKey, defects: condDefects, customDefects: condCustom })
                  )}
                >
                  ✨ Generate note
                </button>
                {condEntry.noteEdited && <span className="sf-note-edited">edited</span>}
              </div>
              <textarea
                className="sf-condition-note"
                rows={3}
                placeholder="Condition note — tap Generate, then edit as needed…"
                value={condEntry.note || ''}
                onChange={e => onConditionChange(substrate, 'note', e.target.value, true)}
              />
            </div>
          )}
        </div>
      )}
      <div className="sf-method-row">
        <div className="sf-method-pills">
          {METHOD_OPTIONS.map(opt => {
            const active = (settings.method || 'brush_roll') === opt.key;
            return (
              <button key={opt.key}
                className={`sf-method-pill ${active ? 'active' : ''} sf-method-${opt.key}`}
                onClick={() => {
                  const cfg = METHOD_CONFIG[opt.key];
                  onSettingChange(substrate, 'method', opt.key);
                  if (cfg.defaultSpeed) {
                    onSettingChange(substrate, 'paintingSpeed', cfg.defaultSpeed);
                  }
                  onSettingChange(substrate, 'methodPrep', cfg.defaultPrep);
                }}>
                {opt.icon} {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      {(settings.method === 'spray' || settings.method === 'abseil') && (() => {
        const cfg = METHOD_CONFIG[settings.method];
        const prep = settings.methodPrep ?? cfg.defaultPrep;
        const dtLabel = settings.method === 'spray' ? 'Spray Downtime' : 'Abseil Downtime';
        return (
          <div className="sf-method-details">
            <label className="sf-setting"><span>{dtLabel}</span>
              <select value={prep} onChange={e => onSettingChange(substrate, 'methodPrep', Number(e.target.value))}>
                <option value={0.15}>15%</option><option value={0.20}>20%</option><option value={0.25}>25%</option>
                <option value={0.30}>30%</option><option value={0.35}>35%</option><option value={0.40}>40%</option>
                <option value={0.50}>50%</option>
              </select>
            </label>
          </div>
        );
      })()}
      {/* Inline dimension field — replaces girth for linear surfaces (Skirting, Fascia, etc.) */}
      {(() => {
        const gd = GIRTH_DISPLAY[base];
        if (!gd || !gd.inline) return null;
        return (
          <div className="sf-inline-dim">
            <label className="sf-inline-dim-label">{gd.label}</label>
            <GirthInput value={settings.girth ?? gd.default ?? DEFAULT_GIRTHS[base] ?? 1} onChange={v => onSettingChange(substrate, 'girth', v)} />
          </div>
        );
      })()}

      {canWrap && wrapOn && (
        <CalculatorPad substrate={substrate} entries={entries} girth={girth}
          sharedDimKey={wrapCfg.sharedDim} sharedDimLabel={wrapCfg.dimLabel}
          sharedDimValue={settings._wrapSharedDim || ''}
          onSharedDimChange={v => {
            onSettingChange(substrate, '_wrapSharedDim', v);
            entries.forEach(e => {
              if (wrapCfg.sharedDim === 'height') onUpdate(e.id, 'height', v);
              else if (wrapCfg.sharedDim === 'width') onUpdate(e.id, 'width', v);
            });
          }}
          onAddRun={(length, label) => {
            const dim = settings._wrapSharedDim || '';
            onAdd(substrate, groupId, { length, label,
              width: wrapCfg.sharedDim === 'width' ? dim : '',
              height: wrapCfg.sharedDim === 'height' ? dim : '',
            });
          }}
          onRemoveEntry={onRemove} />
      )}

      {/* Hide individual entry forms when rapid mode is active — the CalculatorPad handles display */}
      {!(canWrap && wrapOn) && entries.map((entry, i) => (
        <EntryForm key={entry.id} substrate={substrate} baseSurface={base} entry={entry} index={i} total={entries.length}
          onUpdate={(f, v) => onUpdate(entry.id, f, v)}
          onRemove={() => onRemove(entry.id)}
          data={data} onSettingChange={onSettingChange} />
      ))}

      {!(canWrap && wrapOn) && (
        <button className="sf-add-btn" onClick={() => onAdd(substrate, groupId)}>
          + {substrate}
        </button>
      )}
      {!(canWrap && wrapOn) && !groupId && onAddIndependent && (
        <button className="sf-add-btn sf-add-independent" onClick={() => onAddIndependent(substrate, base)}>
          + {substrate} (new settings)
        </button>
      )}
    </div>
  );
}
